/**
 * Coffee Bean Clicker v2 - Prestige & Ascension Panel
 * Stage 1: Golden Bean prestige + upgrades
 * Stage 2: Star Dust ascension + upgrades
 */

import * as PIXI from 'pixi.js';
import type { GameCore } from '@app-games/core';
import type { GameState, GameAction } from '../../game/index.js';
import {
  canPrestige, canAscend,
  computePrestigeGoldenBeans, computeAscensionStarDust,
  formatNumber,
} from '../../game/index.js';
import {
  PRESTIGE_UPGRADES, STAR_UPGRADES,
  type PrestigeUpgradeDef, type StarUpgradeDef,
} from '../../game/data/upgrades.js';
import { t } from '../../i18n/index.js';
import { THEME, LAYOUT } from './theme.js';
import {
  drawGlassCard, drawGoldCard, drawPillButton, makeText,
} from './UIHelpers.js';
import { ScrollContainer } from './ScrollContainer.js';

interface UpgradeRow {
  container: PIXI.Container;
  gfx: PIXI.Graphics;
  nameText: PIXI.Text;
  descText: PIXI.Text;
  btnGfx: PIXI.Graphics;
  btnText: PIXI.Text;
  def: PrestigeUpgradeDef | StarUpgradeDef;
  kind: 'prestige' | 'star';
}

export class PrestigePanel extends PIXI.Container {
  private core: GameCore<GameState, GameAction>;
  private scroll: ScrollContainer;
  private panelW: number;

  // Stage 1 elements
  private s1Container = new PIXI.Container();
  private s1TitleText!: PIXI.Text;
  private s1DescText!: PIXI.Text;
  private s1CurrencyText!: PIXI.Text;
  private s1ExpectedText!: PIXI.Text;
  private s1BtnGfx = new PIXI.Graphics();
  private s1BtnText!: PIXI.Text;
  private s1UpgradeLabel!: PIXI.Text;
  private s1LockedText!: PIXI.Text;
  private prestigeRows: UpgradeRow[] = [];

  // Stage 2 elements
  private s2Container = new PIXI.Container();
  private s2TitleText!: PIXI.Text;
  private s2DescText!: PIXI.Text;
  private s2CurrencyText!: PIXI.Text;
  private s2ExpectedText!: PIXI.Text;
  private s2BtnGfx = new PIXI.Graphics();
  private s2BtnText!: PIXI.Text;
  private s2UpgradeLabel!: PIXI.Text;
  private s2LockedText!: PIXI.Text;
  private starRows: UpgradeRow[] = [];

  constructor(w: number, h: number, core: GameCore<GameState, GameAction>) {
    super();
    this.core = core;
    this.panelW = w;

    this.scroll = new ScrollContainer(w, h);
    this.addChild(this.scroll);

    this.buildStage1();
    this.buildStage2();
  }

  private buildStage1(): void {
    const content = this.scroll.content;
    content.addChild(this.s1Container);

    const pad = LAYOUT.padding;

    // Title
    this.s1TitleText = makeText(
      `\u2728 ${t('prestige.title')}`,
      18, THEME.text.gold, { fontWeight: 'bold' }
    );
    this.s1TitleText.x = pad;
    this.s1Container.addChild(this.s1TitleText);

    // Description
    this.s1DescText = makeText(t('prestige.desc'), 12, THEME.text.secondary, {
      wordWrap: true, wordWrapWidth: this.panelW - pad * 2,
    });
    this.s1DescText.x = pad;
    this.s1Container.addChild(this.s1DescText);

    // Currency display
    this.s1CurrencyText = makeText('', 16, THEME.text.gold, { fontWeight: 'bold' });
    this.s1CurrencyText.x = pad;
    this.s1Container.addChild(this.s1CurrencyText);

    // Expected gain
    this.s1ExpectedText = makeText('', 13, THEME.text.secondary);
    this.s1ExpectedText.x = pad;
    this.s1Container.addChild(this.s1ExpectedText);

    // Prestige button
    this.s1BtnGfx.eventMode = 'static';
    this.s1BtnGfx.cursor = 'pointer';
    this.s1BtnGfx.on('pointerdown', () => {
      this.core.dispatch({ type: 'PRESTIGE' });
    });
    this.s1Container.addChild(this.s1BtnGfx);

    this.s1BtnText = makeText(t('prestige.rebirth'), 14, THEME.text.white, { fontWeight: 'bold' });
    this.s1BtnText.anchor.set(0.5, 0.5);
    this.s1Container.addChild(this.s1BtnText);

    // Locked text
    this.s1LockedText = makeText(t('prestige.locked'), 13, THEME.text.secondary);
    this.s1LockedText.x = pad;
    this.s1Container.addChild(this.s1LockedText);

    // Upgrade section label
    this.s1UpgradeLabel = makeText(
      `\uD83C\uDF1F ${t('prestige.upgrades')}`,
      14, THEME.text.gold, { fontWeight: 'bold' }
    );
    this.s1UpgradeLabel.x = pad;
    this.s1Container.addChild(this.s1UpgradeLabel);

    // Prestige upgrade rows
    for (const def of PRESTIGE_UPGRADES) {
      const row = this.createUpgradeRow(def, 'prestige');
      this.s1Container.addChild(row.container);
      this.prestigeRows.push(row);
    }
  }

  private buildStage2(): void {
    const content = this.scroll.content;
    content.addChild(this.s2Container);

    const pad = LAYOUT.padding;

    // Title
    this.s2TitleText = makeText(
      `\u2B50 ${t('ascend.title')}`,
      18, THEME.accent.purple, { fontWeight: 'bold' }
    );
    this.s2TitleText.x = pad;
    this.s2Container.addChild(this.s2TitleText);

    // Description
    this.s2DescText = makeText(t('ascend.desc'), 12, THEME.text.secondary, {
      wordWrap: true, wordWrapWidth: this.panelW - pad * 2,
    });
    this.s2DescText.x = pad;
    this.s2Container.addChild(this.s2DescText);

    // Currency display
    this.s2CurrencyText = makeText('', 16, THEME.accent.purple, { fontWeight: 'bold' });
    this.s2CurrencyText.x = pad;
    this.s2Container.addChild(this.s2CurrencyText);

    // Expected gain
    this.s2ExpectedText = makeText('', 13, THEME.text.secondary);
    this.s2ExpectedText.x = pad;
    this.s2Container.addChild(this.s2ExpectedText);

    // Ascend button
    this.s2BtnGfx.eventMode = 'static';
    this.s2BtnGfx.cursor = 'pointer';
    this.s2BtnGfx.on('pointerdown', () => {
      this.core.dispatch({ type: 'ASCEND' });
    });
    this.s2Container.addChild(this.s2BtnGfx);

    this.s2BtnText = makeText(t('ascend.ascend'), 14, THEME.text.white, { fontWeight: 'bold' });
    this.s2BtnText.anchor.set(0.5, 0.5);
    this.s2Container.addChild(this.s2BtnText);

    // Locked text
    this.s2LockedText = makeText(t('ascend.locked'), 13, THEME.text.secondary);
    this.s2LockedText.x = pad;
    this.s2Container.addChild(this.s2LockedText);

    // Upgrade section label
    this.s2UpgradeLabel = makeText(
      `\uD83C\uDF1F ${t('ascend.upgrades')}`,
      14, THEME.accent.purple, { fontWeight: 'bold' }
    );
    this.s2UpgradeLabel.x = pad;
    this.s2Container.addChild(this.s2UpgradeLabel);

    // Star upgrade rows
    for (const def of STAR_UPGRADES) {
      const row = this.createUpgradeRow(def, 'star');
      this.s2Container.addChild(row.container);
      this.starRows.push(row);
    }
  }

  private createUpgradeRow(
    def: PrestigeUpgradeDef | StarUpgradeDef,
    kind: 'prestige' | 'star',
  ): UpgradeRow {
    const container = new PIXI.Container();
    const pad = LAYOUT.padding;

    const gfx = new PIXI.Graphics();
    container.addChild(gfx);

    const nameText = makeText('', 13, THEME.text.primary, { fontWeight: 'bold' });
    nameText.x = pad + 12;
    container.addChild(nameText);

    const descText = makeText('', 11, THEME.text.secondary);
    descText.x = pad + 12;
    container.addChild(descText);

    const btnGfx = new PIXI.Graphics();
    btnGfx.eventMode = 'static';
    btnGfx.cursor = 'pointer';
    btnGfx.on('pointerdown', () => {
      if (kind === 'prestige') {
        this.core.dispatch({ type: 'BUY_PRESTIGE_UPGRADE', payload: { upgradeId: def.id } });
      } else {
        this.core.dispatch({ type: 'BUY_STAR_UPGRADE', payload: { upgradeId: def.id } });
      }
    });
    container.addChild(btnGfx);

    const btnText = makeText('', 11, THEME.text.white, { fontWeight: 'bold' });
    btnText.anchor.set(0.5, 0.5);
    container.addChild(btnText);

    return { container, gfx, nameText, descText, btnGfx, btnText, def, kind };
  }

  refresh(state: GameState): void {
    const pad = LAYOUT.padding;
    const rowW = this.panelW - pad * 2;
    const btnW = 72;
    const btnH = 30;

    let y = pad;

    // ═══ Stage 1: Prestige ═══
    this.s1Container.y = 0;
    this.s1TitleText.y = y;
    y += 28;

    this.s1DescText.y = y;
    y += this.s1DescText.height + 8;

    const canP = canPrestige(state);
    const expectedGB = computePrestigeGoldenBeans(state);

    this.s1CurrencyText.text = `${t('prestige.goldenBeans')}: ${formatNumber(state.goldenBeans)}`;
    this.s1CurrencyText.y = y;
    y += 24;

    this.s1ExpectedText.text = `${t('prestige.expected')}: +${formatNumber(expectedGB)}`;
    this.s1ExpectedText.y = y;
    y += 24;

    // Prestige button or locked
    if (canP) {
      this.s1LockedText.visible = false;
      this.s1BtnGfx.visible = true;
      this.s1BtnText.visible = true;

      const pBtnW = Math.min(rowW, 200);
      const pBtnH = 40;
      const pBtnX = pad;

      this.s1BtnGfx.clear();
      drawGoldCard(this.s1BtnGfx, pBtnX, y, pBtnW, pBtnH);
      this.s1BtnText.x = pBtnX + pBtnW / 2;
      this.s1BtnText.y = y + pBtnH / 2;
      this.s1BtnText.style.fill = THEME.text.gold;
      y += pBtnH + 12;
    } else {
      this.s1LockedText.visible = true;
      this.s1LockedText.y = y;
      this.s1BtnGfx.visible = false;
      this.s1BtnText.visible = false;
      y += 24;
    }

    // Upgrade section
    this.s1UpgradeLabel.y = y;
    y += 28;

    for (const row of this.prestigeRows) {
      const def = row.def as PrestigeUpgradeDef;
      const cur = state.prestigeUpgrades[def.id] ?? 0;
      const owned = cur >= def.maxLevel;
      const canBuy = !owned && state.goldenBeans >= def.cost;

      row.container.y = y;

      // Card
      row.gfx.clear();
      if (owned) {
        drawGoldCard(row.gfx, pad, 0, rowW, LAYOUT.rowH);
      } else {
        drawGlassCard(row.gfx, pad, 0, rowW, LAYOUT.rowH);
      }

      row.nameText.text = t(def.nameKey);
      row.nameText.y = LAYOUT.rowH / 2 - 12;
      row.descText.text = t(def.descKey);
      row.descText.y = LAYOUT.rowH / 2 + 6;

      const bx = pad + rowW - btnW - 8;
      const by = (LAYOUT.rowH - btnH) / 2;
      row.btnGfx.clear();
      if (owned) {
        drawPillButton(row.btnGfx, bx, by, btnW, btnH, THEME.button.disabled);
        row.btnText.text = '\u2705';
      } else {
        const color = canBuy ? THEME.button.prestige : THEME.button.disabled;
        drawPillButton(row.btnGfx, bx, by, btnW, btnH, color);
        row.btnText.text = `${def.cost}`;
      }
      row.btnText.x = bx + btnW / 2;
      row.btnText.y = by + btnH / 2;

      y += LAYOUT.rowH + LAYOUT.gap;
    }

    y += 16;

    // ═══ Stage 2: Ascension ═══
    const showAscend = canAscend(state);
    this.s2Container.visible = showAscend || state.stats.totalPrestiges >= 5;

    if (this.s2Container.visible) {
      this.s2Container.y = 0;
      this.s2TitleText.y = y;
      y += 28;

      this.s2DescText.y = y;
      y += this.s2DescText.height + 8;

      const expectedSD = computeAscensionStarDust(state);

      this.s2CurrencyText.text = `${t('ascend.starDust')}: ${formatNumber(state.starDust)}`;
      this.s2CurrencyText.y = y;
      y += 24;

      this.s2ExpectedText.text = `${t('ascend.expected')}: +${formatNumber(expectedSD)}`;
      this.s2ExpectedText.y = y;
      y += 24;

      if (showAscend) {
        this.s2LockedText.visible = false;
        this.s2BtnGfx.visible = true;
        this.s2BtnText.visible = true;

        const aBtnW = Math.min(rowW, 200);
        const aBtnH = 40;
        const aBtnX = pad;

        this.s2BtnGfx.clear();
        drawPillButton(this.s2BtnGfx, aBtnX, y, aBtnW, aBtnH, THEME.button.star);
        this.s2BtnText.x = aBtnX + aBtnW / 2;
        this.s2BtnText.y = y + aBtnH / 2;
        y += aBtnH + 12;
      } else {
        this.s2LockedText.visible = true;
        this.s2LockedText.y = y;
        this.s2BtnGfx.visible = false;
        this.s2BtnText.visible = false;
        y += 24;
      }

      // Star upgrade section
      this.s2UpgradeLabel.y = y;
      y += 28;

      for (const row of this.starRows) {
        const def = row.def as StarUpgradeDef;
        const cur = state.starUpgrades[def.id] ?? 0;
        const owned = cur >= def.maxLevel;
        const canBuy = !owned && state.starDust >= def.cost;

        row.container.y = y;

        row.gfx.clear();
        if (owned) {
          // Purple-tinted card for star upgrades
          row.gfx.beginFill(THEME.accent.purple, 0.08);
          row.gfx.drawRoundedRect(pad, 0, rowW, LAYOUT.rowH, LAYOUT.cardRadius);
          row.gfx.endFill();
          row.gfx.lineStyle(1, THEME.accent.purple, 0.2);
          row.gfx.drawRoundedRect(pad, 0, rowW, LAYOUT.rowH, LAYOUT.cardRadius);
          row.gfx.lineStyle(0);
        } else {
          drawGlassCard(row.gfx, pad, 0, rowW, LAYOUT.rowH);
        }

        row.nameText.text = t(def.nameKey);
        row.nameText.y = LAYOUT.rowH / 2 - 12;
        row.descText.text = t(def.descKey);
        row.descText.y = LAYOUT.rowH / 2 + 6;

        const bx = pad + rowW - btnW - 8;
        const by = (LAYOUT.rowH - btnH) / 2;
        row.btnGfx.clear();
        if (owned) {
          drawPillButton(row.btnGfx, bx, by, btnW, btnH, THEME.button.disabled);
          row.btnText.text = '\u2705';
        } else {
          const color = canBuy ? THEME.button.star : THEME.button.disabled;
          drawPillButton(row.btnGfx, bx, by, btnW, btnH, color);
          row.btnText.text = `${def.cost}`;
        }
        row.btnText.x = bx + btnW / 2;
        row.btnText.y = by + btnH / 2;

        y += LAYOUT.rowH + LAYOUT.gap;
      }
    }

    y += pad;
    this.scroll.setContentHeight(y);
  }

  onResize(w: number, h: number): void {
    this.panelW = w;
    this.scroll.resize(w, h);
    this.s1DescText.style.wordWrapWidth = w - LAYOUT.padding * 2;
    this.s2DescText.style.wordWrapWidth = w - LAYOUT.padding * 2;
  }

  updateScroll(dt: number): void {
    this.scroll.update(dt);
  }
}
